import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

// Date utilities
export function formatDate(date, options = {}) {
  const { fallback = 'Not available', ...intlOptions } = options
  
  // Handle null, undefined, empty string
  if (!date || date === '' || date === null || date === undefined) {
    return fallback
  }
  
  const dateObj = new Date(date)
  
  // Check if the date is valid
  if (isNaN(dateObj.getTime())) {
    return fallback === 'original' ? String(date) : fallback
  }
  
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    ...intlOptions
  }).format(dateObj)
}

export function formatTime(date) {
  // Handle null, undefined, empty string, or invalid dates
  if (!date || date === '' || date === null || date === undefined) {
    return 'Not available'
  }
  
  const dateObj = new Date(date)
  
  // Check if the date is valid
  if (isNaN(dateObj.getTime())) {
    return 'Not available'
  }
  
  return new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  }).format(dateObj)
}

export function getRelativeTime(date) {
  // Handle null, undefined, empty string, or invalid dates
  if (!date || date === '' || date === null || date === undefined) {
    return 'Not available'
  }
  
  const now = new Date()
  const target = new Date(date)
  
  // Check if the date is valid
  if (isNaN(target.getTime())) {
    return 'Not available'
  }
  
  const diffInSeconds = Math.floor((now - target) / 1000)
  
  if (diffInSeconds < 60) return 'just now'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`
  
  return formatDate(date)
}

// Number utilities
export function formatNumber(num, decimals = 0) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num)
}

// Display up to `maxDecimals` and trim trailing zeros.
// Examples with maxDecimals=2: 1 -> "1", 1.2 -> "1.2", 1.239 -> "1.24"
export function formatNumberSmart(value, maxDecimals = 2) {
  if (value === null || value === undefined || value === '') return ''
  const numeric = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(numeric)) return String(value)

  const rounded = Number(numeric.toFixed(maxDecimals))
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals
  }).format(rounded)
}

export function formatDuration(minutes) {
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
}

// Fitness utilities
export function calculateBMI(weight, height) {
  // weight in kg, height in cm
  const heightInMeters = height / 100
  return weight / (heightInMeters * heightInMeters)
}

export function getBMICategory(bmi) {
  if (bmi < 18.5) return { category: 'Underweight', color: 'text-blue-600' }
  if (bmi < 25) return { category: 'Normal', color: 'text-green-600' }
  if (bmi < 30) return { category: 'Overweight', color: 'text-yellow-600' }
  return { category: 'Obese', color: 'text-red-600' }
}

export function calculateCaloriesBurned(activity, duration, weight = 70) {
  // Simplified MET values for common activities
  const metValues = {
    walking: 3.8,
    running: 8.0,
    cycling: 6.8,
    swimming: 6.0,
    weightlifting: 3.0,
    yoga: 2.5,
    dancing: 4.8,
    hiking: 6.0
  }
  
  const met = metValues[activity.toLowerCase()] || 4.0
  return Math.round((met * weight * duration) / 60)
}

// Color utilities
export function getProgressColor(percentage) {
  if (percentage >= 100) return 'text-green-600 bg-green-100'
  if (percentage >= 75) return 'text-blue-600 bg-blue-100'
  if (percentage >= 50) return 'text-yellow-600 bg-yellow-100'
  return 'text-gray-600 bg-gray-100'
}

export function getStreakColor(days) {
  if (days >= 30) return 'text-purple-600 bg-purple-100'
  if (days >= 14) return 'text-green-600 bg-green-100'
  if (days >= 7) return 'text-blue-600 bg-blue-100'
  return 'text-gray-600 bg-gray-100'
}

// Validation utilities
export function validateEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export function validatePassword(password) {
  return {
    isValid: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumbers: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  }
}

// Local storage utilities
export function getStorageItem(key, defaultValue = null) {
  try {
    const item = localStorage.getItem(key)
    return item ? JSON.parse(item) : defaultValue
  } catch (error) {
    console.error(`Error reading from localStorage:`, error)
    return defaultValue
  }
}

export function setStorageItem(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.error(`Error writing to localStorage:`, error)
  }
}

// Array utilities
export function groupBy(array, key) {
  return array.reduce((groups, item) => {
    const group = item[key]
    groups[group] = groups[group] || []
    groups[group].push(item)
    return groups
  }, {})
}

export function sortBy(array, key, direction = 'asc') {
  return [...array].sort((a, b) => {
    const aVal = a[key]
    const bVal = b[key]
    
    if (direction === 'desc') {
      return bVal > aVal ? 1 : bVal < aVal ? -1 : 0
    }
    return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
  })
}

// Debounce utility
export function debounce(func, wait) {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}
